import React, { useMemo, useState, useEffect } from 'react';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';

import { Flex, FlexColumn } from 'src/components/flex';
import FreshnessGroupAccordion from 'src/pages/landlord/conversations/components/list/FreshnessGroupAccordion';
import NoSearchResults from 'src/pages/landlord/conversations/components/list/ConversationsNoSearchResults';
import type { IConversationMatchWithChannel, EConversationTab } from 'src/pages/landlord/conversations/context/types';
import { EConversationCategory, conversationCategories, initialConversationsPagesByCategory } from 'src/pages/landlord/conversations/context/types';
import type { IConversation } from 'src/api/landlord-messages-api';
import type { TConversationsFilter } from 'src/services/api/messages/use-conversations-search';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useNavigateConversationsWithArrowKeys } from 'src/pages/landlord/conversations/components/key-listener/hooks';
import { GroupByOption } from 'src/pages/landlord/conversations/components/header/GroupBySelector';
import PropertyGroupAccordion from 'src/pages/landlord/conversations/components/PropertyGroupAccordion';
import type { IProperty } from 'src/services/api';

type Props = {
    tab?: EConversationTab;
    search: string;
    filter: TConversationsFilter;
    conversationsByCategory: Record<EConversationCategory, IConversation[]>;
    selectedConversations: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations: React.Dispatch<React.SetStateAction<Array<IConversation | IConversationMatchWithChannel>>>;
    focusedConversation?: IConversation;
    setFocusedConversation: React.Dispatch<React.SetStateAction<IConversation | undefined>>;
    setMobileMenuAnchorEl: (anchorEl: HTMLElement | undefined) => void;
    groupBy?: GroupByOption;
    propertyGroups?: Array<[number | null, IConversation[]]>;
    propertiesById?: Map<number, IProperty>;
};

const ROWS_PER_PAGE = 25;

const ConversationsList = ({
    search: searchQuery,
    filter: searchFilter,
    conversationsByCategory,
    selectedConversations,
    setSelectedConversations,
    focusedConversation,
    setFocusedConversation,
    setMobileMenuAnchorEl,
    groupBy,
    propertyGroups,
    propertiesById,
}: Props) => {
    const { leadsByUuid } = useAuthenticatedState();

    // Local pagination state
    const [pagesByCategory, setPagesByCategory] = useState(initialConversationsPagesByCategory);

    // 'Unread from offline teammates' only appears on the system inbox with
    // presence enabled; hide its header entirely when it has no conversations.
    const categories = conversationCategories.filter(o => {
        if (o === 'Archived') {
            return false;
        }
        if (o === EConversationCategory.UnreadOffline) {
            return conversationsByCategory[o].length > 0;
        }
        return true;
    });

    // Count total filtered conversations across all groups
    const totalFilteredConversations = useMemo(() => {
        if (groupBy === GroupByOption.PROPERTY && propertyGroups) {
            return propertyGroups.reduce((sum, [, convs]) => sum + convs.length, 0);
        }
        return conversationCategories.reduce((sum, cat) => {
            return sum + conversationsByCategory[cat].length;
        }, 0);
    }, [conversationsByCategory, groupBy, propertyGroups]);

    // Compute visible conversations by category (with pagination)
    const visibleConversationsByCategory = useMemo(() => {
        return conversationCategories.reduce((acc, cat) => {
            const _conversations = conversationsByCategory[cat];
            const _page = pagesByCategory[cat];
            return {
                ...acc,
                [cat]: _conversations.slice((_page - 1) * ROWS_PER_PAGE, _page * ROWS_PER_PAGE),
            };
        }, {} as Record<EConversationCategory, IConversation[]>);
    }, [conversationsByCategory, pagesByCategory]);

    // Flatten visible conversations across all groups for arrow key navigation
    const flattenedVisibleConversations = useMemo(() => {
        if (groupBy === GroupByOption.PROPERTY && propertyGroups) {
            return propertyGroups.flatMap(([, convs]) => convs);
        }
        return conversationCategories.reduce((acc: IConversation[], cat) => {
            return [...acc, ...visibleConversationsByCategory[cat]];
        }, []);
    }, [visibleConversationsByCategory, groupBy, propertyGroups]);

    // Arrow key navigation through visible conversations
    useNavigateConversationsWithArrowKeys({
        visibleConversations: flattenedVisibleConversations,
        focusedConversation,
        setFocusedConversation,
    });

    // Reset pages when conversations by category changes
    useEffect(() => {
        setPagesByCategory(initialConversationsPagesByCategory);
    }, [totalFilteredConversations]);

    // Check if search or filter is active
    const isSearchOrFilterActive = !!searchQuery ||
        !!searchFilter?.assigneeIds?.length ||
        !!searchFilter?.ingestionSources?.length ||
        !!searchFilter?.leadStages?.length ||
        !!searchFilter?.leadLabelIds?.length ||
        !!searchFilter?.propertyIds?.length ||
        !!searchFilter?.propertyLabelIds?.length;

    const isSearchingWithoutResults = isSearchOrFilterActive && totalFilteredConversations === 0;

    const INITIAL_PROPERTY_GROUPS = 10;
    const [propertyGroupLimit, setPropertyGroupLimit] = useState(INITIAL_PROPERTY_GROUPS);

    // Reset limit when property groups change (e.g. filter applied)
    useEffect(() => {
        setPropertyGroupLimit(INITIAL_PROPERTY_GROUPS);
    }, [propertyGroups]);

    const visiblePropertyGroups = propertyGroups?.slice(0, propertyGroupLimit);
    const hasMorePropertyGroups = (propertyGroups?.length ?? 0) > propertyGroupLimit;

    return (
        <FlexColumn gap={2} py={3} alignItems="center">
            {!isSearchingWithoutResults && groupBy === GroupByOption.PROPERTY && visiblePropertyGroups?.map(([propertyId, groupConversations]) => {
                const property = propertyId !== null ? propertiesById?.get(propertyId) : undefined;
                const propertyName = property?.name ?? (propertyId !== null ? 'Unknown Property' : 'No Property');

                return (
                    <PropertyGroupAccordion
                        key={propertyId ?? 'no-property'}
                        title={propertyName}
                        conversations={groupConversations}
                        leadsByUuid={leadsByUuid!}
                        selectedConversations={selectedConversations}
                        setSelectedConversations={setSelectedConversations}
                        focusedConversation={focusedConversation}
                        setFocusedConversation={setFocusedConversation}
                        property={property}
                        setMobileMenuAnchorEl={setMobileMenuAnchorEl}
                        variant="longPreview"
                    />
                );
            })}

            {!isSearchingWithoutResults && groupBy !== GroupByOption.PROPERTY && categories.map((cat) => (
                <FreshnessGroupAccordion
                    key={cat}
                    category={cat}
                    conversationsByCategory={conversationsByCategory}
                    visibleConversationsByCategory={visibleConversationsByCategory}
                    leadsByUuid={leadsByUuid!}
                    selectedConversations={selectedConversations}
                    setSelectedConversations={setSelectedConversations}
                    focusedConversation={focusedConversation}
                    setFocusedConversation={setFocusedConversation}
                    rowsPerPage={ROWS_PER_PAGE}
                    pagesByCategory={pagesByCategory}
                    setPagesByCategory={setPagesByCategory}
                    setMobileMenuAnchorEl={setMobileMenuAnchorEl}
                />
            ))}

            {isSearchingWithoutResults && (
                <NoSearchResults />
            )}

            {(isSearchOrFilterActive || (groupBy === GroupByOption.PROPERTY && hasMorePropertyGroups)) && (
                <Flex gap={2} alignItems="center" flexWrap="nowrap" p={0} m={0} sx={{
                    '& .MuiButton-root': {
                        '@media (max-width: 900px)': { maxWidth: '50vw' }
                    }
                }}
                >
                    {groupBy === GroupByOption.PROPERTY && hasMorePropertyGroups && (
                        <ShowMorePropertiesButton
                            remaining={(propertyGroups?.length ?? 0) - propertyGroupLimit}
                            onClick={() => setPropertyGroupLimit(prev => prev + INITIAL_PROPERTY_GROUPS)}
                        />
                    )}
                    {isSearchOrFilterActive && (
                        <SearchInArchiveButton searchQuery={searchQuery} searchFilter={searchFilter} />
                    )}
                </Flex>
            )}
        </FlexColumn >
    );
};

function ShowMorePropertiesButton({ remaining, onClick }: { remaining: number; onClick: () => void }) {
    return (
        <Button variant="contained" onClick={onClick}>
            Show More Properties
            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                &nbsp;({remaining} remaining)
            </Box>
        </Button>
    );
}

function SearchInArchiveButton({ searchQuery, searchFilter }: { searchQuery: string; searchFilter: TConversationsFilter }) {
    const navigate = useNavigate();

    return (
        <Button
            role="link"
            variant="contained"
            onClick={() => {
                const params = new URLSearchParams();
                if (searchFilter) {
                    for (const [key, value] of Object.entries(searchFilter)) {
                        if (value && !(Array.isArray(value) && value.length === 0)) {
                            params.append(key, String(value));
                        }
                    }
                }
                if (searchQuery) {
                    params.append('q', searchQuery || '');
                }
                navigate('/landlord/inner/search?' + params.toString());
            }}
            startIcon={<ArchiveIcon sx={{ '@media (max-width: 900px)': { display: 'none' } }} />}
            data-testid="search-archived-btn"
        >
            Search In Archive
        </Button>
    );
}

export default ConversationsList;
